import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Container,
  Button,
  Spinner,
  Alert,
  Row,
  Col,
} from 'react-bootstrap';
import {
  FaArrowLeft,
  FaSync,
  FaBed,
  FaUsers,
  FaCalendarAlt,
  FaInfoCircle,
  FaPrint,
} from 'react-icons/fa';
import pythonApiClient from '../../../services/api/pythonBaseApi';

const fmtFecha = (iso) => {
  if (!iso) return '—';
  const p = String(iso).slice(0, 10).split('-');
  if (p.length === 3) return `${p[2]}/${p[1]}/${p[0]}`;
  return iso;
};

const DonutChart = ({ pct, ocupadas, libres, total }) => {
  const r = 52;
  const c = 2 * Math.PI * r;
  const occupiedLen = (pct / 100) * c;
  return (
    <div className="rh-donut-wrap">
      <svg viewBox="0 0 120 120" className="rh-donut-svg">
        <circle cx="60" cy="60" r={r} fill="none" stroke="#22c55e" strokeWidth="14" />
        <circle
          cx="60"
          cy="60"
          r={r}
          fill="none"
          stroke="#dc2626"
          strokeWidth="14"
          strokeDasharray={`${occupiedLen} ${c}`}
          strokeLinecap="butt"
          transform="rotate(-90 60 60)"
        />
      </svg>
      <div className="rh-donut-center">
        <div className="rh-donut-pct">{pct}%</div>
        <div className="rh-donut-label">OCUPACIÓN</div>
      </div>
      <div className="rh-donut-legend mt-3">
        <span className="rh-leg rh-leg-ocup">
          OCUPADAS {ocupadas} ({pct}%)
        </span>
        <span className="rh-leg rh-leg-libre">
          LIBRES {libres} ({total > 0 ? (100 - pct).toFixed(1) : 0}%)
        </span>
      </div>
    </div>
  );
};

const NivelBar = ({ pct, nivel }) => {
  const color =
    nivel === 'critico' ? '#dc2626' : nivel === 'alerta' ? '#f59e0b' : '#22c55e';
  return (
    <div className="rh-nivel-bar">
      <div className="rh-nivel-track">
        <div
          className="rh-nivel-fill"
          style={{ width: `${Math.min(pct, 100)}%`, background: color }}
        />
        <span className="rh-nivel-value">{pct}%</span>
      </div>
      <div className="rh-nivel-scale">
        <span>0%</span>
        <span>50%</span>
        <span>100%</span>
      </div>
      <div className="rh-nivel-leyenda">
        <span className="rh-nivel-opt">ÓPTIMO &lt; 80%</span>
        <span className="rh-nivel-ale">ALERTA 80% – 90%</span>
        <span className="rh-nivel-cri">CRÍTICO &gt; 90%</span>
      </div>
    </div>
  );
};

const TablaDistribucion = ({ titulo, filas, totalLabel, totalRowClass, subtotales }) => {
  const totVar = filas.reduce((s, f) => s + f.varones, 0);
  const totMuj = filas.reduce((s, f) => s + f.mujeres, 0);
  const totAll = filas.reduce((s, f) => s + f.total, 0);
  const st = subtotales || {};

  return (
    <div className="rh-table-panel">
      <div className="rh-table-title">{titulo}</div>
      <table className="rh-table">
        <thead>
          <tr>
            <th>MÓDULO / CAMPAMENTO</th>
            <th className="text-center">VARONES</th>
            <th className="text-center">MUJERES</th>
            <th className="text-center">TOTAL</th>
          </tr>
        </thead>
        <tbody>
          {filas.map((f, i) => (
            <tr
              key={f.modulo_id != null ? `mod-${f.modulo_id}` : `row-${i}`}
              className={i % 2 ? 'rh-tr-alt' : ''}
            >
              <td>{f.modulo}</td>
              <td className="text-center">{f.varones}</td>
              <td className="text-center">{f.mujeres}</td>
              <td className="text-center fw-semibold">{f.total}</td>
            </tr>
          ))}
          {st.habilitadas_staff?.total > 0 && (
            <>
              <tr className="rh-tr-sub">
                <td colSpan={4}>CAMAS STAFF / AUX. Y TEC.</td>
              </tr>
              <tr className="rh-tr-subtotal">
                <td>Subtotal</td>
                <td className="text-center">{st.habilitadas_staff.varones}</td>
                <td className="text-center">{st.habilitadas_staff.mujeres}</td>
                <td className="text-center">{st.habilitadas_staff.total}</td>
              </tr>
            </>
          )}
          {st.habilitadas_worker?.total > 0 && (
            <>
              <tr className="rh-tr-sub">
                <td colSpan={4}>CAMAS WORKER / OPERADORES</td>
              </tr>
              <tr className="rh-tr-subtotal">
                <td>Subtotal</td>
                <td className="text-center">{st.habilitadas_worker.varones}</td>
                <td className="text-center">{st.habilitadas_worker.mujeres}</td>
                <td className="text-center">{st.habilitadas_worker.total}</td>
              </tr>
            </>
          )}
        </tbody>
        <tfoot>
          <tr className={totalRowClass}>
            <td>{totalLabel}</td>
            <td className="text-center">{totVar}</td>
            <td className="text-center">{totMuj}</td>
            <td className="text-center">{totAll}</td>
          </tr>
        </tfoot>
      </table>
    </div>
  );
};

const BarChartModulos = ({ items }) => {
  const max = Math.max(...items.map((x) => x.habilitadas), 1);
  return (
    <div className="rh-bar-chart">
      {items.map((m) => (
        <div key={m.modulo} className="rh-bar-row">
          <div className="rh-bar-label" title={m.modulo}>
            {m.modulo}
          </div>
          <div className="rh-bar-tracks">
            <div
              className="rh-bar hab"
              style={{ width: `${(m.habilitadas / max) * 100}%` }}
              title={`Habilitadas: ${m.habilitadas}`}
            />
            <div
              className="rh-bar ocup"
              style={{ width: `${(m.ocupadas / max) * 100}%` }}
              title={`Ocupadas: ${m.ocupadas}`}
            />
          </div>
          <div className="rh-bar-nums">
            <span className="text-primary">{m.habilitadas}</span> /{' '}
            <span className="text-danger">{m.ocupadas}</span>
          </div>
        </div>
      ))}
      <div className="rh-bar-legend mt-2">
        <span><i className="rh-sq hab" /> Habilitadas</span>
        <span><i className="rh-sq ocup" /> Ocupadas</span>
      </div>
    </div>
  );
};

const ReporteHabitabilidad = () => {
  const navigate = useNavigate();
  const selectedProject = JSON.parse(
    localStorage.getItem('selectedProject') || 'null'
  );
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [data, setData] = useState(null);

  const cargar = useCallback(async () => {
    if (!selectedProject?.id) return;
    setLoading(true);
    setError('');
    try {
      const res = await pythonApiClient.get('/reportes/hoteleria/habitabilidad/', {
        headers: { 'X-Proyecto-ID': selectedProject.id },
      });
      if (res.data?.success) {
        setData(res.data.data);
      } else {
        setError(res.data?.message || 'No se pudo cargar el reporte');
      }
    } catch (err) {
      const d = err.response?.data?.detail ?? err.response?.data?.message;
      setError(typeof d === 'string' ? d : 'Error al cargar el reporte');
    } finally {
      setLoading(false);
    }
  }, [selectedProject?.id]);

  useEffect(() => {
    cargar();
  }, [cargar]);

  if (!selectedProject?.id) {
    return (
      <Container className="p-5">
        <Alert variant="warning">Seleccione un proyecto para ver el reporte.</Alert>
      </Container>
    );
  }

  const enc = data?.encabezado || {};
  const kpis = data?.kpis || {};
  const pct = kpis.porcentaje_ocupacion ?? 0;

  return (
    <>
      <style>{`
        .rh-page { background: #e8ecf1; min-height: 100vh; padding-bottom: 2rem; }
        .rh-banner {
          background: linear-gradient(135deg, #002060 0%, #1e3a8a 100%);
          color: #fff;
          padding: 1.25rem 0;
        }
        .rh-banner h1 { font-size: 1.35rem; font-weight: 800; letter-spacing: 0.02em; margin: 0; }
        .rh-banner-date { font-size: 0.95rem; opacity: 0.95; }
        .rh-meta {
          background: #fff;
          border-bottom: 2px solid #002060;
          padding: 0.75rem 0;
          font-size: 0.85rem;
        }
        .rh-meta span { margin-right: 2rem; }
        .rh-meta strong { color: #002060; }
        .rh-kpi {
          background: #fff;
          border-radius: 4px;
          overflow: hidden;
          box-shadow: 0 2px 8px rgba(0,0,0,0.08);
          height: 100%;
        }
        .rh-kpi-head {
          color: #fff;
          font-size: 0.7rem;
          font-weight: 700;
          padding: 0.5rem 0.75rem;
          text-transform: uppercase;
        }
        .rh-kpi-body { padding: 1rem; display: flex; align-items: center; gap: 0.75rem; }
        .rh-kpi-num { font-size: 2rem; font-weight: 800; line-height: 1; }
        .rh-kpi-hab .rh-kpi-head { background: #002060; }
        .rh-kpi-ocup .rh-kpi-head { background: #dc2626; }
        .rh-kpi-libre .rh-kpi-head { background: #16a34a; }
        .rh-kpi-pct .rh-kpi-head { background: #ea580c; }
        .rh-panel {
          background: #fff;
          border-radius: 4px;
          box-shadow: 0 2px 8px rgba(0,0,0,0.08);
          padding: 1.25rem;
          height: 100%;
        }
        .rh-panel-title {
          font-weight: 700;
          font-size: 0.8rem;
          color: #002060;
          margin-bottom: 1rem;
          text-transform: uppercase;
        }
        .rh-donut-wrap { position: relative; max-width: 200px; margin: 0 auto; }
        .rh-donut-svg { width: 100%; display: block; }
        .rh-donut-center {
          position: absolute; top: 50%; left: 50%;
          transform: translate(-50%, -55%);
          text-align: center;
        }
        .rh-donut-pct { font-size: 1.25rem; font-weight: 800; color: #002060; }
        .rh-donut-label { font-size: 0.65rem; color: #64748b; }
        .rh-leg { display: block; font-size: 0.75rem; margin: 0.2rem 0; }
        .rh-leg-ocup::before { content: ''; display: inline-block; width: 10px; height: 10px; background: #dc2626; margin-right: 6px; }
        .rh-leg-libre::before { content: ''; display: inline-block; width: 10px; height: 10px; background: #22c55e; margin-right: 6px; }
        .rh-nivel-track {
          position: relative; height: 28px; background: #e2e8f0; border-radius: 4px; overflow: hidden;
        }
        .rh-nivel-fill { height: 100%; transition: width 0.4s; }
        .rh-nivel-value {
          position: absolute; right: 8px; top: 50%; transform: translateY(-50%);
          font-weight: 700; font-size: 0.85rem; color: #1e293b;
        }
        .rh-nivel-scale { display: flex; justify-content: space-between; font-size: 0.7rem; color: #94a3b8; margin-top: 4px; }
        .rh-nivel-leyenda { display: flex; flex-wrap: wrap; gap: 0.75rem; margin-top: 0.75rem; font-size: 0.7rem; }
        .rh-nivel-opt { color: #16a34a; }
        .rh-nivel-ale { color: #ea580c; }
        .rh-nivel-cri { color: #dc2626; }
        .rh-table-panel { background: #fff; border-radius: 4px; box-shadow: 0 2px 8px rgba(0,0,0,0.08); overflow: hidden; }
        .rh-table-title {
          background: #f1f5f9; padding: 0.6rem 1rem; font-weight: 700; font-size: 0.75rem;
          color: #002060; border-bottom: 1px solid #cbd5e1;
        }
        .rh-table { width: 100%; font-size: 0.78rem; margin: 0; }
        .rh-table thead th {
          background: #374151; color: #fff; padding: 0.5rem; font-weight: 600;
        }
        .rh-table td { padding: 0.4rem 0.5rem; border-bottom: 1px solid #f1f5f9; }
        .rh-tr-alt { background: #f8fafc; }
        .rh-tr-sub td { background: #e2e8f0; font-weight: 600; font-size: 0.72rem; }
        .rh-tr-subtotal td { background: #f1f5f9; font-weight: 600; }
        .rh-tfoot-hab td { background: #002060 !important; color: #fff; font-weight: 700; }
        .rh-tfoot-libre td { background: #16a34a !important; color: #fff; font-weight: 700; }
        .rh-bar-chart { font-size: 0.72rem; }
        .rh-bar-row { display: grid; grid-template-columns: 140px 1fr 70px; gap: 8px; align-items: center; margin-bottom: 6px; }
        .rh-bar-label { white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
        .rh-bar-tracks { display: flex; flex-direction: column; gap: 3px; }
        .rh-bar { height: 8px; border-radius: 2px; min-width: 2px; }
        .rh-bar.hab { background: #002060; }
        .rh-bar.ocup { background: #dc2626; }
        .rh-sq { display: inline-block; width: 10px; height: 10px; margin-right: 4px; vertical-align: middle; }
        .rh-sq.hab { background: #002060; }
        .rh-sq.ocup { background: #dc2626; }
        .rh-nota { background: #eff6ff; border: 1px solid #bfdbfe; border-radius: 4px; padding: 1rem; font-size: 0.85rem; color: #1e40af; }
        .rh-toolbar { background: #fff; padding: 0.75rem 0; margin-bottom: 1rem; }
        @media print {
          .rh-no-print { display: none !important; }
          .rh-page { background: #fff; }
        }
      `}</style>

      <div className="rh-page">
        <div className="rh-toolbar rh-no-print">
          <Container className="d-flex justify-content-between align-items-center flex-wrap gap-2">
            <Button variant="secondary" size="sm" onClick={() => navigate('/reportes')}>
              <FaArrowLeft className="me-1" /> Reportes
            </Button>
            <div className="d-flex gap-2">
              <Button variant="outline-primary" size="sm" onClick={cargar} disabled={loading}>
                <FaSync className={loading ? 'fa-spin' : ''} /> Actualizar
              </Button>
              <Button variant="outline-secondary" size="sm" onClick={() => window.print()}>
                <FaPrint /> Imprimir
              </Button>
            </div>
          </Container>
        </div>

        {loading && (
          <Container className="text-center py-5">
            <Spinner animation="border" variant="primary" />
            <p className="mt-2 text-muted">Generando reporte de habitabilidad…</p>
          </Container>
        )}

        {error && (
          <Container>
            <Alert variant="danger">{error}</Alert>
          </Container>
        )}

        {data && !loading && (
          <>
            <div className="rh-banner">
              <Container className="d-flex justify-content-between align-items-center">
                <h1>REPORTE DE HABITABILIDAD</h1>
                <div className="rh-banner-date d-flex align-items-center gap-2">
                  <FaCalendarAlt />
                  {fmtFecha(enc.fecha_corte)}
                </div>
              </Container>
            </div>

            <div className="rh-meta">
              <Container>
                <span><strong>PROYECTO:</strong> {enc.proyecto}</span>
                {data?.proyecto_id != null && (
                  <span><strong>ID PROYECTO:</strong> {data.proyecto_id}</span>
                )}
                {data?.total_modulos_listados != null && (
                  <span>
                    <strong>MÓDULOS:</strong> {data.total_modulos_listados} con camas habilitadas
                  </span>
                )}
                <span><strong>RESPONSABLE:</strong> {enc.responsable}</span>
                <span><strong>HORA DE CORTE:</strong> {enc.hora_corte}</span>
              </Container>
            </div>

            <Container className="mt-3">
              <Row className="g-3 mb-3">
                <Col md={3} sm={6}>
                  <div className="rh-kpi rh-kpi-hab">
                    <div className="rh-kpi-head">TOTAL DE CAMAS HABILITADAS</div>
                    <div className="rh-kpi-body">
                      <FaBed size={28} className="text-primary" />
                      <span className="rh-kpi-num">{kpis.total_habilitadas}</span>
                    </div>
                  </div>
                </Col>
                <Col md={3} sm={6}>
                  <div className="rh-kpi rh-kpi-ocup">
                    <div className="rh-kpi-head">TOTAL CAMAS OCUPADAS</div>
                    <div className="rh-kpi-body">
                      <FaBed size={28} className="text-danger" />
                      <span className="rh-kpi-num">{kpis.total_ocupadas}</span>
                    </div>
                  </div>
                </Col>
                <Col md={3} sm={6}>
                  <div className="rh-kpi rh-kpi-libre">
                    <div className="rh-kpi-head">TOTAL CAMAS LIBRES</div>
                    <div className="rh-kpi-body">
                      <FaBed size={28} className="text-success" />
                      <span className="rh-kpi-num">{kpis.total_libres}</span>
                    </div>
                  </div>
                </Col>
                <Col md={3} sm={6}>
                  <div className="rh-kpi rh-kpi-pct">
                    <div className="rh-kpi-head">% OCUPACIÓN</div>
                    <div className="rh-kpi-body">
                      <FaUsers size={28} className="text-warning" />
                      <span className="rh-kpi-num">{pct}%</span>
                    </div>
                  </div>
                </Col>
              </Row>

              <Row className="g-3 mb-3">
                <Col lg={6}>
                  <div className="rh-panel">
                    <div className="rh-panel-title">OCUPACIÓN GENERAL</div>
                    <DonutChart
                      pct={pct}
                      ocupadas={kpis.total_ocupadas}
                      libres={kpis.total_libres}
                      total={kpis.total_habilitadas}
                    />
                  </div>
                </Col>
                <Col lg={6}>
                  <div className="rh-panel">
                    <div className="rh-panel-title">NIVEL DE OCUPACIÓN</div>
                    <NivelBar pct={pct} nivel={kpis.nivel_ocupacion} />
                  </div>
                </Col>
              </Row>

              <Row className="g-3 mb-3">
                <Col lg={6}>
                  <TablaDistribucion
                    titulo="DISTRIBUCIÓN DE CAMAS HABILITADAS"
                    filas={data.distribucion_habilitadas || []}
                    subtotales={data.subtotales}
                    totalLabel="TOTAL CAMAS HABILITADAS"
                    totalRowClass="rh-tfoot-hab"
                  />
                </Col>
                <Col lg={6}>
                  <TablaDistribucion
                    titulo="DISTRIBUCIÓN DE CAMAS LIBRES"
                    filas={data.distribucion_libres || []}
                    totalLabel="TOTAL CAMAS LIBRES"
                    totalRowClass="rh-tfoot-libre"
                  />
                </Col>
              </Row>

              <Row className="g-3">
                <Col lg={8}>
                  <div className="rh-panel">
                    <div className="rh-panel-title">
                      OCUPACIÓN POR MÓDULO (HABILITADAS vs OCUPADAS)
                    </div>
                    <BarChartModulos items={data.grafico_modulos || []} />
                  </div>
                </Col>
                <Col lg={4}>
                  <div className="rh-panel h-100">
                    <div className="rh-panel-title">NOTA</div>
                    <div className="rh-nota">
                      <FaInfoCircle className="me-2" />
                      Los datos corresponden al corte de la fecha indicada.
                      {kpis.total_reservadas > 0 && (
                        <div className="mt-2 small">
                          Camas reservadas (sin check-in):{' '}
                          <strong>{kpis.total_reservadas}</strong>
                        </div>
                      )}
                    </div>
                  </div>
                </Col>
              </Row>
            </Container>
          </>
        )}
      </div>
    </>
  );
};

export default ReporteHabitabilidad;
